import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/loading';
import { useSaveLabOrder } from './hooks/useSaveLabOrder';
import { usePatientsComboBox } from '../hooks/usePatientsComboBox';
import { useLaboratories } from '../hooks/useLaboratories';
import { useProfessionals } from '../hooks/useProfessionals';
import LaboratoryFormModal from '../register/laboratories/laboratoryFormModal';
import { parseApiDate, toMessageText } from './labOrderStatus';

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : null);

export default function LabOrderFormModal({ open, onClose, onSaveSuccess, labOrder }) {
  const navigate = useNavigate();
  const isEditing = Boolean(labOrder);

  const { save, isSaving } = useSaveLabOrder();
  const { patients, isLoading: isPatientsLoading } = usePatientsComboBox();
  const {
    laboratories,
    isLoading: isLaboratoriesLoading,
    refetch: refetchLaboratories,
  } = useLaboratories();
  const [isLaboratoryModalOpen, setLaboratoryModalOpen] = useState(false);
  const { professionals, isLoading: isProfessionalsLoading } = useProfessionals();

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [dentistId, setDentistId] = useState('');
  const [laboratoryId, setLaboratoryId] = useState('');
  const [procedure, setProcedure] = useState('');
  const [tooth, setTooth] = useState('');
  const [shade, setShade] = useState('');
  const [sentDate, setSentDate] = useState(dayjs());
  const [expectedReturnDate, setExpectedReturnDate] = useState(null);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [alreadySent, setAlreadySent] = useState(true);
  const [error, setError] = useState('');

  // An order waiting for pickup has no dates: they are informed by the "Registrar envio" action.
  const hasDates = isEditing ? Boolean(labOrder.sentDate) : alreadySent;

  useEffect(() => {
    if (!open) return;

    setSelectedPatient(
      labOrder ? { id: labOrder.patientId, label: labOrder.patientName ?? '' } : null,
    );
    setDentistId(labOrder?.dentistId ?? '');
    setLaboratoryId(labOrder?.laboratoryId ?? '');
    setProcedure(labOrder?.procedure ?? '');
    setTooth(labOrder?.tooth ?? '');
    setShade(labOrder?.shade ?? '');
    setSentDate(labOrder ? parseApiDate(labOrder.sentDate) : dayjs());
    setExpectedReturnDate(labOrder ? parseApiDate(labOrder.expectedReturnDate) : null);
    setAmount(labOrder?.amount ?? '');
    setNotes(labOrder?.notes ?? '');
    setAlreadySent(true);
    setError('');
  }, [open, labOrder]);

  // Selects the laboratory just added so the user doesn't have to look for it again
  const handleNewLaboratorySaved = (message, savedLaboratory) => {
    refetchLaboratories().then((updatedLaboratories) => {
      const newLaboratory = (updatedLaboratories ?? []).find(
        (laboratory) => laboratory.name === savedLaboratory?.name,
      );
      if (newLaboratory) setLaboratoryId(newLaboratory.id);
    });
  };

  const submit = () => {
    if (!selectedPatient || !laboratoryId || !procedure.trim()) {
      setError('Preencha paciente, laboratório e procedimento.');
      return;
    }
    if (hasDates && (!sentDate || !expectedReturnDate)) {
      setError('Preencha a data de envio e a previsão de retorno.');
      return;
    }
    if (hasDates && dayjs(expectedReturnDate).isBefore(dayjs(sentDate), 'day')) {
      setError('A previsão de retorno não pode ser anterior à data de envio.');
      return;
    }

    const payload = {
      patientId: selectedPatient.id,
      dentistId: dentistId || null,
      laboratoryId,
      procedure: procedure.trim(),
      tooth: tooth.trim() || null,
      shade: shade.trim() || null,
      sentDate: hasDates ? formatDate(sentDate) : null,
      expectedReturnDate: hasDates ? formatDate(expectedReturnDate) : null,
      amount: amount === '' ? null : Number(amount),
      notes: notes.trim() || null,
      alreadySent,
    };

    setError('');
    save(labOrder?.id ?? null, payload)
      .then((returnFromApi) => onSaveSuccess(returnFromApi))
      .catch((returnFromApi) => {
        setError(toMessageText(returnFromApi.response?.data, 'Erro ao salvar envio.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  const isLoading =
    isSaving || isPatientsLoading || isLaboratoriesLoading || isProfessionalsLoading;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Loading isToHidden={isLoading === false} />
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {isEditing ? 'Editar Envio ao Laboratório' : 'Novo Envio ao Laboratório'}
      </DialogTitle>
      <DialogContent dividers>
        <Collapse in={Boolean(error)} sx={{ mb: 1 }}>
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Collapse>

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={12}>
            <Autocomplete
              options={patients}
              getOptionLabel={(option) => option.label ?? ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              )}
              value={selectedPatient}
              onChange={(_, newValue) => setSelectedPatient(newValue ?? null)}
              noOptionsText="Paciente não encontrado."
              renderInput={(params) => <TextField {...params} label="Paciente *" />}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={laboratories}
              getOptionLabel={(option) => option.name ?? ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={laboratories.find((laboratory) => laboratory.id === laboratoryId) ?? null}
              onChange={(_, newValue) => setLaboratoryId(newValue?.id ?? '')}
              noOptionsText="Nenhum laboratório encontrado."
              PaperComponent={({ children, ...other }) => (
                <Paper {...other}>
                  {children}
                  <Box
                    sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                    onMouseDown={(event) => event.preventDefault()}
                  >
                    <Button fullWidth size="small" onClick={() => setLaboratoryModalOpen(true)}>
                      + Adicionar novo laboratório
                    </Button>
                  </Box>
                </Paper>
              )}
              renderInput={(params) => <TextField {...params} label="Laboratório *" />}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              label="Profissional"
              value={dentistId}
              onChange={(event) => setDentistId(event.target.value)}
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {professionals.map((professional) => (
                <MenuItem key={professional.id} value={professional.id}>
                  {professional.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label="Procedimento *"
              placeholder="Ex: Coroa de porcelana"
              value={procedure}
              onChange={(event) => setProcedure(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Dente/Elemento"
              placeholder="Ex: 21"
              value={tooth}
              onChange={(event) => setTooth(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Cor"
              placeholder="Ex: A2"
              value={shade}
              onChange={(event) => setShade(event.target.value)}
            />
          </Grid>

          {!isEditing && (
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={alreadySent}
                    onChange={(event) => setAlreadySent(event.target.checked)}
                  />
                }
                label="Já enviado ao laboratório"
              />
            </Grid>
          )}

          {hasDates && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Data de envio *"
                  value={sentDate}
                  onChange={(date) => setSentDate(date)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Previsão de retorno *"
                  value={expectedReturnDate}
                  onChange={(date) => setExpectedReturnDate(date)}
                  format="DD/MM/YYYY"
                  minDate={sentDate ?? undefined}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Valor"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Observações"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={isSaving}>
          Salvar
        </Button>
      </DialogActions>

      <LaboratoryFormModal
        open={isLaboratoryModalOpen}
        onClose={() => setLaboratoryModalOpen(false)}
        onSave={handleNewLaboratorySaved}
      />
    </Dialog>
  );
}

LabOrderFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  labOrder: PropTypes.shape({
    id: PropTypes.number,
    patientId: PropTypes.number,
    patientName: PropTypes.string,
    dentistId: PropTypes.number,
    laboratoryId: PropTypes.number,
    procedure: PropTypes.string,
    tooth: PropTypes.string,
    shade: PropTypes.string,
    sentDate: PropTypes.string,
    expectedReturnDate: PropTypes.string,
    status: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    notes: PropTypes.string,
  }),
};

LabOrderFormModal.defaultProps = {
  labOrder: null,
};
